import React, { useEffect, useState } from 'react';
import { sendAlertParse } from '../api/contact_api';
import { getContactFormSubmitError, UNSUPPORTED_PLATFORM_ERROR_TEXT } from '../utils/errorHandler';
import { containsUrl, getLinkNotAllowedError, getSupportedPlatformUrl } from '../utils/urlValidation';

/** The one field on this form that may hold a link. */
const LINK_FIELD_LABEL = 'Link to Content';

/**
 * “Alert us!” report modal — shared by the landing page and the database screen.
 */
export default function DatabaseAlertPopup({ isOpen, onClose }) {
  const [formValues, setFormValues] = useState({
    fullName: '',
    emailAddress: '',
    linkToContent: '',
    description: '',
  });
  const [isSending, setIsSending] = useState(false);
  const [popupMessage, setPopupMessage] = useState(null);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateForm = () => {
    const nextErrors = {};

    if (!formValues.fullName.trim()) {
      nextErrors.fullName = 'Full Name is required.';
    } else if (containsUrl(formValues.fullName)) {
      nextErrors.fullName = getLinkNotAllowedError('Full Name', LINK_FIELD_LABEL);
    }
    if (!formValues.emailAddress.trim()) {
      nextErrors.emailAddress = 'Email Address is required.';
    } else if (!emailRegex.test(formValues.emailAddress.trim())) {
      nextErrors.emailAddress = 'Please enter a valid Email Address.';
    } else if (containsUrl(formValues.emailAddress)) {
      nextErrors.emailAddress = getLinkNotAllowedError('Mail Address', LINK_FIELD_LABEL);
    }
    if (!formValues.linkToContent.trim()) {
      nextErrors.linkToContent = 'Link to Content is required.';
    } else if (!getSupportedPlatformUrl(formValues.linkToContent)) {
      nextErrors.linkToContent = UNSUPPORTED_PLATFORM_ERROR_TEXT;
    }
    if (!formValues.description.trim()) {
      nextErrors.description = 'Description is required.';
    } else if (containsUrl(formValues.description)) {
      nextErrors.description = getLinkNotAllowedError('Description', LINK_FIELD_LABEL);
    }

    const firstErrorMessage = Object.values(nextErrors)[0];
    if (firstErrorMessage) {
      setPopupMessage({
        title: 'Please check the form',
        text: firstErrorMessage,
      });
    }
    return Object.keys(nextErrors).length === 0;
  };

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSend = async () => {
    if (!validateForm()) return;

    try {
      setIsSending(true);
      setPopupMessage(null);

      await sendAlertParse({
        fullName: formValues.fullName.trim(),
        emailAddress: formValues.emailAddress.trim(),
        // Validated above, and returned with its protocol filled in.
        linkToContent: getSupportedPlatformUrl(formValues.linkToContent),
        description: formValues.description.trim(),
      });

      setFormValues({
        fullName: '',
        emailAddress: '',
        linkToContent: '',
        description: '',
      });
      setPopupMessage({
        title: 'Message sent.',
        text: 'Thanks, we received your report and will review it shortly.',
      });
    } catch (error) {
      setPopupMessage(getContactFormSubmitError(error));
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="database-alert-popup-overlay" onClick={onClose}>
      <div className="database-alert-popup" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          className="database-alert-popup-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        {popupMessage ? (
          <div className="database-alert-success-popup" role="status" aria-live="polite">
            <button
              type="button"
              className="database-alert-success-popup-close"
              onClick={() => setPopupMessage(null)}
              aria-label="Dismiss message"
            >
              ×
            </button>
            <strong>{popupMessage.title}</strong>
            <span>{popupMessage.text}</span>
          </div>
        ) : null}
        <div className="database-alert-popup-content">
          <h3>Alert us!</h3>
          <p>
            Found online antisemitism that you want to make sure is removed? After reporting with your own account, send CyberWell your examples and we will escalate them to the relevant social media platform.
          </p>
          <div className="database-alert-form">
            <div className="database-alert-inline-fields">
              <div className="database-alert-field-group">
                <label htmlFor="alert-full-name">
                  Full Name<span className="database-alert-required">*</span>
                </label>
                <input
                  id="alert-full-name"
                  type="text"
                  value={formValues.fullName}
                  onChange={handleFieldChange('fullName')}
                  placeholder="Full name"
                />
              </div>
              <div className="database-alert-field-group">
                <label htmlFor="alert-email">
                  Mail Address<span className="database-alert-required">*</span>
                </label>
                <input
                  id="alert-email"
                  type="email"
                  value={formValues.emailAddress}
                  onChange={handleFieldChange('emailAddress')}
                  placeholder="name@example.com"
                />
              </div>
            </div>
            <div className="database-alert-field-group">
              <label htmlFor="alert-link">
                Link to Content<span className="database-alert-required">*</span>
              </label>
              <input
                id="alert-link"
                type="text"
                value={formValues.linkToContent}
                onChange={handleFieldChange('linkToContent')}
                placeholder="https://"
              />
            </div>
            <div className="database-alert-field-group">
              <label htmlFor="alert-description">
                Description<span className="database-alert-required">*</span>
              </label>
              <textarea
                id="alert-description"
                value={formValues.description}
                onChange={handleFieldChange('description')}
                placeholder="Tell us why this content is antisemitic - the more detailed the better!"
                rows={4}
              />
            </div>
          </div>
          <button type="button" className="database-alert-send-btn" onClick={handleSend} disabled={isSending}>
            {isSending ? (
              <>
                <span className="spinner-small database-alert-send-spinner" aria-hidden="true" />
                Sending...
              </>
            ) : 'Send'}
          </button>
        </div>
        <img
          className="database-alert-popup-illustration"
          src="/images/database-alert-popup-illustration.png"
          alt=""
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
